import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  addAdminSongToPlaylist,
  adminLogin,
  adminLogout,
  cancelAdminJob,
  createAdminJob,
  deleteAdminJob,
  getAdminDashboard,
  getAdminJob,
  getAdminSession,
  getAdminSettings,
  getAdminUsers,
  listAdminJobs,
  patchAdminSettings,
  removeAdminSongFromPlaylist,
} from "../api/admin";
import { HttpError } from "../api/client";
import type {
  AdminDashboardResponse,
  AdminEditableSettings,
  AdminJobDetail,
  AdminJobListResponse,
  AdminSession,
  AdminSettingsResponse,
  AdminUsersResponse,
  CreateJobRequest,
  JobStatusResponse,
  PlaylistSummary,
  SongSummary,
} from "../api/types";
import { useNotifications } from "../notifications/useNotifications";
import type { AdminTab } from "./AdminPageTypes";

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof HttpError) return err.toUserMessage();
  if (err instanceof Error) return err.message;
  return fallback;
}

function jobTitle(job: AdminJobDetail | null): string {
  return job?.source_title ?? job?.youtube_video_id ?? job?.youtube_url ?? "";
}

function statusEnvelope(job: AdminJobDetail | null): JobStatusResponse | null {
  if (!job) return null;
  return {
    job_id: job.id,
    status: job.status,
    progress: job.progress,
    current_step: job.current_step,
    error: job.error,
  };
}

export interface AdminDataState {
  activeTab: AdminTab;
  authChecked: boolean;
  dashboard: AdminDashboardResponse | null;
  error: string | null;
  jobPlaylistMembership: ReadonlySet<string>;
  jobs: AdminJobListResponse | null;
  jobStatus: JobStatusResponse | null;
  pending: boolean;
  pendingPlaylistKey: string | null;
  playlists: PlaylistSummary[];
  recentSongs: SongSummary[];
  search: string;
  selectedJob: AdminJobDetail | null;
  selectedSong: SongSummary | null;
  session: AdminSession | null;
  settings: AdminSettingsResponse | null;
  users: AdminUsersResponse | null;
  addToPlaylist: (playlistSlug: string, jobId: string) => Promise<void>;
  cancelJob: () => Promise<void>;
  createJob: (payload: CreateJobRequest) => Promise<void>;
  deleteJob: (jobId: string) => Promise<void>;
  deleteSong: (jobId: string) => Promise<void>;
  login: (password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshCurrentData: () => Promise<void>;
  removeFromPlaylist: (playlistSlug: string, jobId: string) => Promise<void>;
  saveSettings: (payload: AdminEditableSettings) => Promise<void>;
  selectJob: (jobId: string) => Promise<void>;
  setActiveTab: (tab: AdminTab) => void;
  setSearch: (search: string) => void;
}

export function useAdminData(): AdminDataState {
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useNotifications();
  const [session, setSession] = useState<AdminSession | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [activeTab, setActiveTab] = useState<AdminTab>("overview");
  const [dashboard, setDashboard] = useState<AdminDashboardResponse | null>(null);
  const [users, setUsers] = useState<AdminUsersResponse | null>(null);
  const [jobs, setJobs] = useState<AdminJobListResponse | null>(null);
  const [selectedJob, setSelectedJob] = useState<AdminJobDetail | null>(null);
  const [settings, setSettings] = useState<AdminSettingsResponse | null>(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [pendingPlaylistKey, setPendingPlaylistKey] = useState<string | null>(null);

  const loadAdminData = useCallback(async (jobSearch: string) => {
    const [dashboardResponse, usersResponse, jobsResponse, settingsResponse] =
      await Promise.all([
        getAdminDashboard(),
        getAdminUsers(),
        listAdminJobs({ search: jobSearch }),
        getAdminSettings(),
      ]);
    setDashboard(dashboardResponse);
    setUsers(usersResponse);
    setJobs(jobsResponse);
    setSettings(settingsResponse);
    if (jobsResponse.jobs.length > 0) {
      setSelectedJob(await getAdminJob(jobsResponse.jobs[0].id));
    } else {
      setSelectedJob(null);
    }
  }, []);

  const refreshCurrentData = useCallback(async () => {
    await loadAdminData(search);
  }, [loadAdminData, search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const restored = await getAdminSession();
        if (cancelled) return;
        setSession(restored);
        await loadAdminData("");
      } catch {
        if (!cancelled) setSession(null);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAdminData]);

  const playlists = useMemo(
    () => dashboard?.library.playlists ?? [],
    [dashboard?.library.playlists],
  );
  const recentSongs = useMemo(
    () => dashboard?.library.recent_songs ?? [],
    [dashboard?.library.recent_songs],
  );
  const jobStatus = statusEnvelope(selectedJob);
  const selectedSong: SongSummary | null = selectedJob
    ? { job_id: selectedJob.id, title: jobTitle(selectedJob) }
    : null;

  const jobPlaylistMembership = useMemo(() => {
    if (!selectedJob) return new Set<string>();
    return new Set(
      playlists
        .filter((playlist) =>
          playlist.songs.some((song) => song.job_id === selectedJob.id),
        )
        .map((playlist) => playlist.slug),
    );
  }, [playlists, selectedJob]);

  const login = useCallback(async (password: string): Promise<boolean> => {
    setPending(true);
    setError(null);
    try {
      const response = await adminLogin(password);
      setSession(response.session);
      await loadAdminData(search);
      return true;
    } catch (err) {
      const message = errorMessage(err, t("admin.errors.login"));
      setError(message);
      notifyError(message);
      return false;
    } finally {
      setPending(false);
    }
  }, [loadAdminData, notifyError, search, t]);

  const logout = useCallback(async () => {
    setPending(true);
    try {
      await adminLogout();
      setSession(null);
      setDashboard(null);
      setUsers(null);
      setJobs(null);
      setSelectedJob(null);
      setSettings(null);
    } catch (err) {
      notifyError(errorMessage(err, t("admin.errors.logout")));
    } finally {
      setPending(false);
    }
  }, [notifyError, t]);

  const createJob = useCallback(async (payload: CreateJobRequest) => {
    setPending(true);
    setError(null);
    try {
      const created = await createAdminJob(payload);
      await loadAdminData(search);
      setSelectedJob(await getAdminJob(created.job_id));
      setActiveTab("jobs");
      notifySuccess(t("admin.jobs.created"));
    } catch (err) {
      const message = errorMessage(err, t("admin.errors.createJob"));
      setError(message);
      notifyError(message);
    } finally {
      setPending(false);
    }
  }, [loadAdminData, notifyError, notifySuccess, search, t]);

  const selectJob = useCallback(async (jobId: string) => {
    setPending(true);
    try {
      setSelectedJob(await getAdminJob(jobId));
    } catch (err) {
      notifyError(errorMessage(err, t("admin.errors.loadJob")));
    } finally {
      setPending(false);
    }
  }, [notifyError, t]);

  const runDelete = useCallback(
    async (jobId: string, successKey: string, errorKey: string) => {
      setPending(true);
      setError(null);
      try {
        await deleteAdminJob(jobId);
        setSelectedJob((current) => (current && current.id === jobId ? null : current));
        await loadAdminData(search);
        notifySuccess(t(successKey));
      } catch (err) {
        const fallback = t(errorKey);
        const message =
          err instanceof HttpError && err.status === 409
            ? t("admin.jobs.deleteNonTerminal")
            : errorMessage(err, fallback);
        notifyError(message);
      } finally {
        setPending(false);
      }
    },
    [loadAdminData, notifyError, notifySuccess, search, t],
  );

  const deleteJob = useCallback(
    (jobId: string) => runDelete(jobId, "admin.jobs.deleted", "admin.errors.deleteJob"),
    [runDelete],
  );

  const deleteSong = useCallback(
    (jobId: string) => runDelete(jobId, "admin.library.songDeleted", "admin.errors.deleteSong"),
    [runDelete],
  );

  const cancelJob = useCallback(async () => {
    if (!selectedJob) return;
    setPending(true);
    try {
      await cancelAdminJob(selectedJob.id);
      setSelectedJob(await getAdminJob(selectedJob.id));
      await loadAdminData(search);
      notifySuccess(t("admin.jobs.cancelled"));
    } catch (err) {
      notifyError(errorMessage(err, t("admin.errors.cancelJob")));
    } finally {
      setPending(false);
    }
  }, [loadAdminData, notifyError, notifySuccess, search, selectedJob, t]);

  const addToPlaylist = useCallback(async (playlistSlug: string, jobId: string) => {
    const key = `${playlistSlug}:${jobId}`;
    setPendingPlaylistKey(key);
    try {
      await addAdminSongToPlaylist(playlistSlug, jobId);
      await loadAdminData(search);
      notifySuccess(t("notifications.addedToPlaylist", { playlist: playlistSlug }));
    } catch (err) {
      notifyError(errorMessage(err, t("home.errors.addSong")));
    } finally {
      setPendingPlaylistKey(null);
    }
  }, [loadAdminData, notifyError, notifySuccess, search, t]);

  const removeFromPlaylist = useCallback(async (playlistSlug: string, jobId: string) => {
    const key = `${playlistSlug}:${jobId}`;
    setPendingPlaylistKey(key);
    try {
      await removeAdminSongFromPlaylist(playlistSlug, jobId);
      await loadAdminData(search);
      notifySuccess(t("notifications.removedFromPlaylist", { playlist: playlistSlug }));
    } catch (err) {
      notifyError(errorMessage(err, t("home.errors.removeSong")));
    } finally {
      setPendingPlaylistKey(null);
    }
  }, [loadAdminData, notifyError, notifySuccess, search, t]);

  const saveSettings = useCallback(async (payload: AdminEditableSettings) => {
    setPending(true);
    try {
      const updated = await patchAdminSettings(payload);
      setSettings(updated);
      setDashboard((previous) => previous ? { ...previous, settings: updated } : previous);
      notifySuccess(t("admin.settings.saved"));
    } catch (err) {
      notifyError(errorMessage(err, t("admin.errors.saveSettings")));
    } finally {
      setPending(false);
    }
  }, [notifyError, notifySuccess, t]);

  return {
    activeTab,
    authChecked,
    dashboard,
    error,
    jobPlaylistMembership,
    jobs,
    jobStatus,
    pending,
    pendingPlaylistKey,
    playlists,
    recentSongs,
    search,
    selectedJob,
    selectedSong,
    session,
    settings,
    users,
    addToPlaylist,
    cancelJob,
    createJob,
    deleteJob,
    deleteSong,
    login,
    logout,
    refreshCurrentData,
    removeFromPlaylist,
    saveSettings,
    selectJob,
    setActiveTab,
    setSearch,
  };
}
